import { Stage, KpiStrip } from "../components/primitives";
import { fmtCurr, fmtNum } from "../utils";

const PERIOD_DAYS_ELAPSED = new Date().getDate();
const PERIOD_TOTAL_DAYS   = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();

function pct(n) {
  if (n == null || n === 0) return "—";
  return `${(n * 100).toFixed(2)}%`;
}

function freqAlert(freq) {
  if (freq >= 3.5) return { label: "Freq. Crítica", color: "var(--danger)", bg: "var(--danger-soft)" };
  if (freq >= 3.0) return { label: "Freq. Alta",    color: "var(--warn)",   bg: "var(--warn-soft)" };
  return null;
}

function ctrAlert(ctr) {
  if ((ctr * 100) < 0.5) return { label: "CTR Baixo", color: "var(--warn)", bg: "var(--warn-soft)" };
  return null;
}

function budgetColor(ratio) {
  if (ratio >= 0.9) return "var(--danger)";
  if (ratio >= 0.75) return "var(--warn)";
  if (ratio >= 0.5)  return "var(--info)";
  return "var(--success)";
}

function Metric({ label, value, alert }) {
  return (
    <div style={{ minWidth: 80 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--faint)", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: alert ? alert.color : "var(--ink)" }}>
        {value}
      </div>
    </div>
  );
}

function ClientKPICard({ kpi, client }) {
  const budget   = client?.orcamento_total;
  const ratio    = budget ? kpi.spend / budget : null;
  const pctBudg  = ratio ? Math.min(ratio * 100, 100) : 0;
  const projected = budget ? (kpi.spend / PERIOD_DAYS_ELAPSED) * PERIOD_TOTAL_DAYS : null;
  const freqA    = freqAlert(kpi.frequency);
  const ctrA     = ctrAlert(kpi.ctr);
  const hasAlert = freqA || ctrA || (ratio && ratio > 0.9);

  return (
    <div style={{
      background: "var(--surface)",
      border: `1px solid ${hasAlert ? "var(--warn)" : "var(--line)"}`,
      borderLeft: hasAlert ? "3px solid var(--warn)" : `1px solid ${"var(--line)"}`,
      borderRadius: 10,
      padding: "20px 22px",
      boxShadow: "0 1px 3px rgba(0,0,0,.06)",
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>{client?.name || kpi.windsor_account_id}</div>
          <div style={{ fontSize: 11, color: "var(--faint)", marginTop: 2 }}>Meta Ads · Mai 2026</div>
        </div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {freqA && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: freqA.bg, color: freqA.color }}>
              {freqA.label}
            </span>
          )}
          {ctrA && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: ctrA.bg, color: ctrA.color }}>
              {ctrA.label}
            </span>
          )}
        </div>
      </div>

      {/* Budget bar */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 5 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>
            {fmtCurr(kpi.spend)} investido
          </span>
          {budget ? (
            <span style={{ fontSize: 11, color: "var(--faint)" }}>
              Budget: {fmtCurr(budget)} · {Math.round(pctBudg)}%
            </span>
          ) : (
            <span style={{ fontSize: 11, color: "var(--warn)" }}>Budget não definido</span>
          )}
        </div>
        {budget && (
          <>
            <div style={{ height: 6, background: "var(--line-soft)", borderRadius: 99 }}>
              <div style={{
                height: 6, borderRadius: 99,
                width: `${pctBudg}%`,
                background: budgetColor(ratio),
                transition: "width .4s",
              }} />
            </div>
            {projected && (
              <div style={{ fontSize: 11, color: projected > budget ? "var(--danger)" : "var(--faint)", marginTop: 4 }}>
                Projeção fim de mês: {fmtCurr(projected)}
                {projected > budget && <strong> · Risco de estouro</strong>}
              </div>
            )}
          </>
        )}
      </div>

      {/* Metrics */}
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
        <Metric label="CTR"        value={pct(kpi.ctr)}          alert={ctrA}  />
        <Metric label="CPC"        value={fmtCurr(kpi.cpc)}                    />
        <Metric label="Frequência" value={fmtNum(kpi.frequency)}  alert={freqA} />
        <Metric label="Alcance"    value={fmtNum(kpi.reach)}                    />
        <Metric label="Cliques"    value={fmtNum(kpi.clicks)}                   />
        {kpi.leads > 0 && <Metric label="Leads" value={fmtNum(kpi.leads)} />}
        {kpi.cpl > 0   && <Metric label="CPL"   value={fmtCurr(kpi.cpl)}  />}
      </div>

      <div style={{ marginTop: 10, fontSize: 10, color: "var(--faint)" }}>
        Atualizado em: {kpi.synced_at ? new Date(kpi.synced_at).toLocaleString("pt-BR") : "—"}
      </div>
    </div>
  );
}

export function PerformanceView({ kpis, clients }) {
  const sorted      = [...kpis].sort((a, b) => (b.spend || 0) - (a.spend || 0));
  const totalSpend  = kpis.reduce((s, k) => s + (k.spend || 0), 0);
  const totalLeads  = kpis.reduce((s, k) => s + (k.leads || 0), 0);
  const avgCTR      = kpis.length ? kpis.reduce((s, k) => s + (k.ctr || 0), 0) / kpis.length : 0;
  const alertCount  = kpis.filter(k => freqAlert(k.frequency) || ctrAlert(k.ctr)).length;

  return (
    <div className="view" style={{ maxWidth: 1040 }}>

      <Stage
        path="performance"
        title="Performance"
        hl="Meta Ads"
        lead={<>Windsor.ai · Mai 2026 · {kpis.length} conta{kpis.length !== 1 ? "s" : ""} sincronizada{kpis.length !== 1 ? "s" : ""}</>}
        actions={
          <span className="stage-path" style={{ fontSize: 11.5 }}>
            <span>atualizar: <b>"atualiza dados Windsor"</b></span>
          </span>
        }
      />

      <KpiStrip style={{ marginBottom: 28 }} items={[
        { label: "Total investido", value: fmtCurr(totalSpend), sub: "no mês" },
        { label: "Total leads", value: totalLeads > 0 ? fmtNum(totalLeads) : "—", sub: "conversões" },
        { label: "CTR médio", value: pct(avgCTR), sub: "todas as contas" },
        { label: "Alertas", value: alertCount, sub: alertCount > 0 ? "rever contas" : "tudo ok", tone: alertCount > 0 ? "bad" : "ok", color: alertCount > 0 ? "var(--danger)" : undefined },
      ]} />

      {/* Cards */}
      {kpis.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "var(--faint)", fontSize: 14 }}>
          Nenhum dado disponível. Peça ao Claude "atualiza dados Windsor".
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {sorted.map((kpi) => (
            <ClientKPICard
              key={kpi.id}
              kpi={kpi}
              client={clients.find(c => c.id === kpi.client_id)}
            />
          ))}
        </div>
      )}

      {/* Luciano Lacerda note */}
      <div style={{
        marginTop: 24, padding: "12px 16px",
        background: "var(--line-soft)", borderRadius: 8,
        fontSize: 12, color: "var(--muted)", lineHeight: 1.6,
      }}>
        <strong style={{ color: "var(--ink)" }}>Ação pendente — Luciano Lacerda:</strong> conta Windsor{" "}
        <code style={{ fontSize: 11, background: "var(--line)", padding: "1px 6px", borderRadius: 3 }}>1501452530601438</code>{" "}
        não vinculada. Há dois clientes: <em>LAGOS</em> e <em>Paisagista</em>. Confirme qual é qual e atualize o campo{" "}
        <code style={{ fontSize: 11 }}>windsor_account_id</code> no CRM.
      </div>
    </div>
  );
}
